import { AnimatePresence, motion, useInView } from "framer-motion";
import { ChevronDown, Linkedin, Mail, Moon, Sun } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import MaltIcon from "../icons/MaltIcon";

type HeroProps = {
  toggleTheme: () => void;
  dark: boolean;
  scrollTo: (ref: React.RefObject<HTMLDivElement>) => void;
  heroRef: React.RefObject<HTMLDivElement>;
  skillRef: React.RefObject<HTMLDivElement>;
  projectsRef: React.RefObject<HTMLDivElement>;
  experienceRef: React.RefObject<HTMLDivElement>;
};

const Hero = ({ toggleTheme, dark, scrollTo, heroRef, skillRef, projectsRef, experienceRef }: HeroProps) => {
  const [showBackToTop, setShowBackToTop] = useState(false);

  const rightZoneRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLElement | null>(null);

  // Hero visible → bouton caché
  const isInView = useInView(rightZoneRef, { amount: 0.5 });

  useEffect(() => {
    const scrollContainer = document.querySelector("#main") as HTMLElement | null;
    scrollContainerRef.current = scrollContainer;
    if (!scrollContainer) return;

    const handleScroll = () => {
      const offset = 300;
      const scrollY = scrollContainer.scrollTop;
      document.body.style.backgroundPositionY = `${offset - scrollY * 0.25}px`;

      // bouton "back to top"
      if (heroRef.current) {
        const rectHero = heroRef.current.getBoundingClientRect();
        const heroVisible = rectHero.top >= 0 && rectHero.bottom > window.innerHeight / 2;
        setShowBackToTop(!heroVisible);
      }
    };

    scrollContainer.addEventListener("scroll", handleScroll);
    handleScroll();

    return () => scrollContainer.removeEventListener("scroll", handleScroll);
  }, [heroRef]);

  const [rect, setRect] = useState<DOMRect | null>(null);

  useEffect(() => {
    if (rightZoneRef.current) {
      const updateRect = () => setRect(rightZoneRef.current!.getBoundingClientRect());
      updateRect();
      window.addEventListener("resize", updateRect);
      return () => window.removeEventListener("resize", updateRect);
    }
  }, []);


  return (
    <section
      ref={heroRef}
      className="relative flex flex-col md:flex-row items-center md:justify-between px-8 md:px-20 min-h-screen md:snap-start text-gray-900 dark:text-white"
    >
      {/* Fixed top-right buttons */}
      <div className="fixed top-4 right-6 z-40 flex gap-2 text-sm font-medium">
        <AnimatePresence>
          {showBackToTop && (
            <motion.button
              key="backToTop"
              onClick={() => scrollTo(heroRef)}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ duration: 0.3 }}
              className="p-2 rounded-full bg-white dark:bg-gray-700 shadow hover:text-blue-600 transition"
              aria-label="Back to Top"
            >
              <ChevronDown className="rotate-180" />
            </motion.button>
          )}
        </AnimatePresence>

        <button
          onClick={toggleTheme}
          className="p-2 rounded-full bg-white dark:bg-gray-700 shadow hover:text-blue-600 transition"
        >
          {dark ? <Sun size={24} /> : <Moon size={24} />}
        </button>
        <a
          href="mailto:florian.ganderatz@gmail.com"
          className="p-2 rounded-full bg-white dark:bg-gray-700 shadow hover:text-blue-600 transition"
        >
          <Mail size={24} />
        </a>
        <a
          href="https://www.linkedin.com/in/florian-ganderatz-092a25188/"
          target="_blank"
          rel="noreferrer"
          className="p-2 rounded-full bg-white dark:bg-gray-700 shadow hover:text-blue-600 transition"
        >
          <Linkedin size={24} />
        </a>
        <a
          href="https://www.malt.fr/profile/florianganderatz"
          target="_blank"
          rel="noreferrer"
          className="p-2 rounded-full bg-white dark:bg-gray-700 shadow hover:text-blue-600 transition"
        >
          <MaltIcon width={24} />
        </a>
      </div>

      {/* Left: Text */}
      <div className="w-full md:w-1/2 text-center md:text-left mt-20 md:mt-0">
        <h1 className="text-5xl md:text-6xl font-extrabold mb-4 tracking-tight ralewaytitle">
          Florian Ganderatz
        </h1>
        <p className="text-xl italic text-gray-600 dark:text-gray-300 ralewaytext">
          French passionate Fullstack Developer,<br />
          I bring my technical expertise and creativity for your project.
        </p>

        <div className="mt-10 flex flex-col items-center gap-4 md:items-start ralewaytext">
          <button
            onClick={() => scrollTo(skillRef)}
            className="w-full md:w-2/3 bg-white dark:bg-gray-800 px-6 py-3 rounded-xl shadow hover:shadow-md text-sm font-semibold"
          >
            🧠 SKILLS
          </button>
          <button
            onClick={() => scrollTo(projectsRef)}
            className="w-full md:w-2/3 bg-white dark:bg-gray-800 px-6 py-3 rounded-xl shadow hover:shadow-md text-sm font-semibold"
          >
            💼 PROJECTS
          </button>
          <button
            onClick={() => scrollTo(experienceRef)}
            className="w-full md:w-2/3 bg-white dark:bg-gray-800 px-6 py-3 rounded-xl shadow hover:shadow-md text-sm font-semibold"
          >
            📈 EXPERIENCE
          </button>
          <a
            href="mailto:florian.ganderatz@gmail.com"
            className="w-full md:w-2/3 bg-white dark:bg-gray-800 text-center px-6 py-3 rounded-xl shadow hover:shadow-md text-sm font-semibold"
          >
            🤝 CONTACT ME
          </a>
        </div>

        <div
          onClick={() => scrollTo(skillRef)}
          className="absolute bottom-8 inset-x-0 hidden md:flex justify-center animate-bounce cursor-pointer z-50"
        >
          <ChevronDown size={32} />
        </div>
      </div>

      {/* Zone droite pour ancrer l'image */}
      <div ref={rightZoneRef} className="w-full mx-6 md:m-0 md:w-1/2 p-1 aspect-square"/>

      {/* Logo animé */}
      <AnimatePresence>
        <motion.div
          className="md:fixed absolute z-30 pointer-events-none"
          animate={
            window.innerWidth <= 1024 && rect != null ?
            {
              top: rect.top + window.scrollY,
              left: rect.left + window.scrollX,
              width: rect.width,
            }
            :
            isInView && rect != null
              ? 
                {
                  top: rect.top + window.scrollY,
                  left: rect.left + window.scrollX,
                  width: rect.width,
                }
              : 
                { 
                  top: 16, left: 16, width: "10%" 
                }
          }          
          transition={{ duration: 0.8 }}
        >
          <div
            className="relative aspect-square overflow-hidden"
            style={{
              WebkitMaskImage: "url(./asset/blob-mask.svg)",
              maskImage: "url(./asset/blob-mask.svg)",
              WebkitMaskSize: "cover",
              maskSize: "cover",
              WebkitMaskRepeat: "no-repeat",
              maskRepeat: "no-repeat",
              WebkitMaskPosition: "center",
              maskPosition: "center",
            }}
          >
            <motion.img
              key={"default"}
              src="./asset/me-draw.png"
              alt="Me"
              className="absolute inset-0 scale-[0.7] z-10"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
            />
            <motion.img
              key={dark ? "night" : "day"}
              src={dark ? "./asset/night.png" : "./asset/day.png"}
              alt="Background"
              className="absolute inset-0 w-full object-cover object-left-top"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.6 }}
            />
          </div>
        </motion.div>
      </AnimatePresence>
    </section>
  );
};

export default Hero;
